/*
  IMPORTANT: this file cannot reference any modules that are related to react & redux
  this is because service-worker runs under a different scope and cannot use those modules
*/

import { DbObjectStore2 } from 'types/db-object-store2';
import { encrypt, decrypt } from 'services/crypto-service';
import { ENVIRONMENT } from 'config';
import { migratoToVersion7 } from 'migrations/1.3-to-1.4';
import { migratoToVersion8 } from 'migrations/1.4-to-1.5';
import { migrateToVersion10 } from 'migrations/1.5-1.5ODAE';

export class Database {

  // IMPORTANT: any time the IndexedDB is updated (adding or deleting object stores), need to update the dbVersion
  public config = {
    dbName: 'OptelCerta',
    dbVersion: 12,
    indexes: {
      Pending: 'Pending',
      Complete: 'Complete',
      Alert: 'Alert',
      Tenant: 'Tenant',
      Token: 'Token',
      ConnectionStatus: 'ConnectionStatus',
      User: 'User',
      AuditLog: 'AuditLog',
      Device: 'Device',
      ApplicationLog: 'ApplicationLog',
      MasterDataProduct: 'MasterDataProduct'
    }
  };
  private db: IDBDatabase;
  private windowDb: IDBFactory;

  constructor(windowDbFactory?: Function) {
    this.windowDb = windowDbFactory ? windowDbFactory() : getWindowDb();
    if (!this.windowDb) {
      window.alert('Your browser doesn\'t support a stable version of IndexedDB.');
    }

    this.init = this.init.bind(this);
    this.add = this.add.bind(this);
    this.read = this.read.bind(this);
    this.readAll = this.readAll.bind(this);
    this.put = this.put.bind(this);
    this.remove = this.remove.bind(this);
    this.clear = this.clear.bind(this);
    this.encrypt = this.encrypt.bind(this);
    this.decrypt = this.decrypt.bind(this);
    this.encryptString = this.encryptString.bind(this);
  }

  public async init(dbName: string, dbVersion: number, indexNames: string[]) {
    const cypheredIndexNames: string[] = [];
    for (let i = 0; i < indexNames.length; i++) {
      cypheredIndexNames.push(await this.encryptString(indexNames[i]));
    }

    return new Promise((resolve, reject) => {
      const request: IDBOpenDBRequest = this.windowDb.open(dbName, dbVersion);
      request.onerror = (event: Event) => {
        reject('error: ' + event);
      };
      request.onsuccess = (_: Event) => {
        this.db = request.result;
        resolve(this.db);
      };
      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        this.db = request.result;
        this.refreshObjectStores(this.db, cypheredIndexNames, event);
      };
    });
  }

  // tslint:disable-next-line:no-any
  public add(indexName: string, item: any, key: string, transaction?: IDBTransaction) {
    return new Promise(async (resolve, reject) => {
      const cypheredIndexName = await this.encryptString(indexName);
      const cypheredKey = await this.encryptString(key);
      const trans = transaction ? transaction : this.db.transaction([cypheredIndexName], 'readwrite');
      const request = trans
        .objectStore(cypheredIndexName)
        .add(await this.encrypt(item), cypheredKey);
      request.onsuccess = (_: Event) => {
        resolve(item);
      };
      request.onerror = (event: Event) => {
        reject(event);
      };
    });
  }

  public read(indexName: string, key: string, transaction?: IDBTransaction) {
    const me = this;
    return new Promise(async (resolve, reject) => {
      const cypheredIndexName = await this.encryptString(indexName);
      const cypheredKey = await this.encryptString(key);
      const trans = transaction ? transaction : this.db.transaction([cypheredIndexName], 'readwrite');
      const request = trans
        .objectStore(cypheredIndexName)
        .get(cypheredKey);

      request.onsuccess = async (_: Event) => {
        resolve(await me.decrypt(request.result));
      };
      request.onerror = (event: Event) => {
        reject(event);
      };
    });
  }

  // tslint:disable-next-line:no-any
  public readAll(indexName: string, transaction?: IDBTransaction): Promise<any[]> {
    const me = this;
    return new Promise(async (resolve, reject) => {
      const cypheredIndexName = await this.encryptString(indexName);
      const trans = transaction ? transaction : this.db.transaction([cypheredIndexName], 'readwrite');
      const objectStore = trans.objectStore(cypheredIndexName) as DbObjectStore2;
      const request = objectStore.getAll();
      request.onsuccess = async (_: Event) => {
        const cypheredResult = request.result as string[];
        const result: string[] = [];
        if (cypheredResult) {
          for (let i = 0; i < cypheredResult.length; i++) {
            result.push(await me.decrypt(cypheredResult[i]));
          }
        }
        resolve(result);
      };
      request.onerror = (event: Event) => {
        reject(event);
      };
    });
  }

  // tslint:disable-next-line:no-any
  public put(indexName: string, item: any, key: string, transaction?: IDBTransaction) {
    return new Promise(async (resolve, reject) => {
      const cypheredIndexName = await this.encryptString(indexName);
      const cypheredKey = await this.encryptString(key);
      const trans = transaction ? transaction : this.db.transaction([cypheredIndexName], 'readwrite');
      const request = trans.objectStore(cypheredIndexName)
        .put(await this.encrypt(item), cypheredKey);
      request.onsuccess = (_: Event) => {
        resolve(item);
      };
      request.onerror = (event: Event) => {
        reject(event);
      };
    });
  }

  public remove(indexName: string, key: string, transaction?: IDBTransaction) {
    return new Promise(async (resolve, reject) => {
      const cypheredIndexName = await this.encryptString(indexName);
      const cypheredKey = await this.encryptString(key);
      const trans = transaction ? transaction : this.db.transaction([cypheredIndexName], 'readwrite');
      const request = trans
        .objectStore(cypheredIndexName)
        .delete(cypheredKey);
      request.onsuccess = (_: Event) => {
        resolve(request.result);
      };
      request.onerror = (event: Event) => {
        reject(event);
      };
    });
  }

  public clear(indexName: string, transaction?: IDBTransaction) {
    return new Promise(async (resolve, reject) => {
      const cypheredIndexName = await this.encryptString(indexName);
      const trans = transaction ? transaction : this.db.transaction([cypheredIndexName], 'readwrite');
      const request = trans
        .objectStore(cypheredIndexName)
        .clear();
      request.onsuccess = (_: Event) => {
        resolve(request.result);
      };
      request.onerror = (event: Event) => {
        reject(event);
      };
    });
  }

  private refreshObjectStores(db: IDBDatabase, indexNames: string[], event: IDBVersionChangeEvent) {
    // if we need to drop indexes we can do it here
    if (db.objectStoreNames.length > 0 && event.oldVersion < 4 && db.objectStoreNames.contains('Client')) {
      db.deleteObjectStore('Client');
    }

    if (db.objectStoreNames.length > 0 && event.oldVersion < 6) {
      indexNames.forEach(indexName => {
        if (db.objectStoreNames.contains(indexName)) {
          db.deleteObjectStore(indexName);
        }
      });
    }

    // IMPORTANT: when performing migrations, we must pass the upgradeEvent transaction
    //            otherwise the upgrade will cause an excpetion with an unclear message

    if (event.target) {
       // tslint:disable-next-line:no-string-literal
      const transaction = event.target['transaction'];

      if (db.objectStoreNames.length > 0 && event.oldVersion < 7) {
        migratoToVersion7(this, db, indexNames, transaction);
      }

      if (db.objectStoreNames.length > 0 && event.oldVersion < 8) {
        migratoToVersion8(this, transaction);
      }

      if (db.objectStoreNames.length > 0 && event.oldVersion < 10) {
        migrateToVersion10(this, transaction);
      }
    }

    indexNames.forEach(indexName => {
      if (db.objectStoreNames.length === 0 || !db.objectStoreNames.contains(indexName)) {
        db.createObjectStore(indexName);
      }
    });
  }

  private async encryptString(text: string) {
    if (text === null || text === undefined) {
      return '';
    }
    if (ENVIRONMENT === 'DEV') {
      return text;
    }
    // Prepend with 'a' to make sure key and object store always start with a letter (firefox)
    return 'a' + await encrypt(text);
  }

  private async encrypt(obj: object) {
    if (ENVIRONMENT === 'DEV') {
      return obj;
    }
    return await encrypt(JSON.stringify(obj));
  }

  private async decrypt(cypheredJson: string) {
    if (ENVIRONMENT === 'DEV') {
      return cypheredJson;
    }
    if (cypheredJson === null || cypheredJson === undefined) {
      return cypheredJson;
    }
    return JSON.parse(await decrypt(cypheredJson));
  }
}

export async function getDatabase(): Promise<Database> {
  const db = new Database();
  const indexes = [
    db.config.indexes.Alert,
    db.config.indexes.Tenant,
    db.config.indexes.Complete,
    db.config.indexes.Pending,
    db.config.indexes.Token,
    db.config.indexes.ConnectionStatus,
    db.config.indexes.User,
    db.config.indexes.AuditLog,
    db.config.indexes.Device,
    db.config.indexes.ApplicationLog,
    db.config.indexes.MasterDataProduct
  ];
  await db.init(db.config.dbName, db.config.dbVersion, indexes);
  return db;
}

// TODO: make database compatible with all browsers (#29118)
export function getWindowDb() {
  return self.indexedDB; // || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB;
}


// WEBPACK FOOTER //
// ./src/database.ts